/**
 * P3-1 分配规则配置（admin 专属）
 *
 * v1.2-rc3：新增"第 1 段·主管分配策略 (Layer-1)"
 * 全段：① 主管分配(Layer-1) ② 流程开关 ③ 销售权重 ④ 硬性规则 ⑤ 紧急情况 ⑥ 当前 Top 10
 */
import { useState, useEffect, useMemo } from 'react';
import { Switch, Slider, InputNumber, Radio, Select, Input, Modal, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { $ } from '../../lib/api';
import { PageHeader, HomeCard, Btn, Pill, TableWrap } from '../../components/ui';
// v1.4 RBAC 阶段 4 P1-#3：director 进此页能看但保存按钮 disable（投流规则写仅 admin）
import { useHasPermission } from '../../lib/permissions';

const WEIGHT_FIELDS = [
  { key: 'weightCloseRate',          label: '成单率',           hint: '最近 30 天成单数 / 分配数' },
  { key: 'weightTargetProgress',     label: '业绩完成率',       hint: '本月营收 / 本月目标' },
  { key: 'weightFollowupCompletion', label: '跟踪完整率',       hint: '近 30 天有跟进客户数 / 持客数' },
  { key: 'weightActiveCustomers',    label: '客户数量（反向）', hint: '当前持客越多分越低，避免过载' },
  { key: 'weightResponseSpeed',      label: '响应速度',         hint: '近 30 天 lead 平均接受秒数' },
  { key: 'weightTeamPerformance',    label: '团队表现',         hint: '所在团队整体业绩' },
];

export function LeadRoutingConfigPage() {
  const qc = useQueryClient();
  // v1.4 RBAC P1-#3：director 进此页能看但不能改（lead:configure_routing 仅 admin）
  const canEdit = useHasPermission('lead:configure_routing');
  const { data: cfg } = useQuery<any>({
    queryKey: ['lead-routing-config'],
    queryFn: () => $.get('/lead-routing-config'),
  });
  const [local, setLocal] = useState<any>(null);
  useEffect(() => { if (cfg) setLocal({ ...cfg }); }, [cfg]);

  const save = useMutation({
    mutationFn: () => $.put('/lead-routing-config', local),
    onSuccess: () => {
      message.success('已保存');
      qc.invalidateQueries({ queryKey: ['lead-routing-config'] });
    },
    onError: (e: any) => message.error(e?.message ?? '保存失败'),
  });

  const sum = useMemo(() => {
    if (!local) return 0;
    return WEIGHT_FIELDS.reduce((s, f) => s + Number(local[f.key] ?? 0), 0);
  }, [local]);

  if (!local) return null;

  return (
    <>
      <PageHeader
        breadcrumb={['Admin', '分配规则配置']}
        title="📊 线索分配规则"
        subtitle="新线索按这些规则自动分配给销售员"
        actions={
          <Btn kind="primary"
               onClick={() => save.mutate()}
               disabled={!canEdit || sum !== 100 || save.isPending}>
            {save.isPending ? '保存中…' : !canEdit ? '只读（需 admin 权限）' : '保存配置'}
          </Btn>
        }
      />
      <div className="work-content">
        {/* v1.2-rc3：Layer-1 主管分配段 — 放最前 */}
        <ManagerAllocationSection local={local} setLocal={setLocal} />

        <HomeCard title="第 2 段 · 流程开关">
          <div style={{ padding: 16, display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16 }}>
            <Toggle label="启用 AI 预分析" desc="收到线索瞬间生成画像 + 推荐"
                    checked={!!local.enableAiPreAnalysis}
                    onChange={(v) => setLocal({ ...local, enableAiPreAnalysis: v ? 1 : 0 })} />
            <Toggle label="启用自动分配" desc="关闭则全部 pending 由主管手动"
                    checked={!!local.enableAutoRouting}
                    onChange={(v) => setLocal({ ...local, enableAutoRouting: v ? 1 : 0 })} />
            <Toggle label="主管确认" desc="开启则 AI 推荐后等主管点击；关闭直接分给 Top 1"
                    checked={!!local.enableManagerApproval}
                    onChange={(v) => setLocal({ ...local, enableManagerApproval: v ? 1 : 0 })} />
          </div>
        </HomeCard>

        <HomeCard title={
          <>第 3 段 · 销售员综合得分权重 <span style={{
            marginLeft: 8, fontSize: 12, color: sum === 100 ? 'var(--success)' : 'var(--danger)', fontWeight: 500,
          }}>总和 {sum} / 100</span></>
        }>
          <div style={{ padding: '8px 16px 16px' }}>
            {WEIGHT_FIELDS.map((f) => (
              <div key={f.key} style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontWeight: 500, minWidth: 130 }}>{f.label}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{f.hint}</span>
                  <span style={{ marginLeft: 'auto', minWidth: 40, textAlign: 'right' }}>
                    {local[f.key]}%
                  </span>
                </div>
                <Slider
                  min={0} max={50}
                  value={local[f.key]}
                  onChange={(v) => setLocal({ ...local, [f.key]: v })}
                />
              </div>
            ))}
          </div>
        </HomeCard>

        <HomeCard title="第 4 段 · 硬性规则">
          <div style={{ padding: 16, display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 16 }}>
            <Field label="单销售员每日新线索上限">
              <InputNumber value={local.dailyNewLeadCap}
                onChange={(v) => setLocal({ ...local, dailyNewLeadCap: Number(v) })}
                min={1} max={1000} />
            </Field>
            <Field label="单销售员手上活跃客户上限">
              <InputNumber value={local.activeCustomerCap}
                onChange={(v) => setLocal({ ...local, activeCustomerCap: Number(v) })}
                min={10} max={2000} />
            </Field>
            <Toggle label="排除低业绩销售员"
                    desc="开启后下方阈值生效"
                    checked={!!local.excludeLowPerformers}
                    onChange={(v) => setLocal({ ...local, excludeLowPerformers: v ? 1 : 0 })} />
            <Field label="低业绩阈值（业绩完成率 %）">
              <InputNumber value={local.lowPerformerThreshold}
                onChange={(v) => setLocal({ ...local, lowPerformerThreshold: Number(v) })}
                min={0} max={100} disabled={!local.excludeLowPerformers} />
            </Field>
          </div>
        </HomeCard>

        <HomeCard title="第 5 段 · 紧急情况">
          <div style={{ padding: 16, display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 16 }}>
            <Toggle label="高意向（≥4 星）必须 Top 3"
                    desc="低意向可降级到 Top 5"
                    checked={!!local.highIntentRouteToTop}
                    onChange={(v) => setLocal({ ...local, highIntentRouteToTop: v ? 1 : 0 })} />
            <Field label="主管决策超时（秒）">
              <InputNumber value={local.managerDecisionTimeoutSeconds}
                onChange={(v) => setLocal({ ...local, managerDecisionTimeoutSeconds: Number(v) })}
                min={60} max={86400} step={60} />
            </Field>
            <Field label="销售员响应超时（秒）">
              <InputNumber value={local.salesResponseTimeoutSeconds}
                onChange={(v) => setLocal({ ...local, salesResponseTimeoutSeconds: Number(v) })}
                min={60} max={86400} step={60} />
            </Field>
          </div>
        </HomeCard>

        <HomeCard title="第 6 段 · 当前最高分销售员 Top 10">
          <PreviewPanel />
        </HomeCard>
      </div>
    </>
  );
}

function Toggle({ label, desc, checked, onChange }: {
  label: string; desc?: string; checked: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <div style={{
      padding: 12, background: 'var(--bg-2)', borderRadius: 6,
      display: 'flex', alignItems: 'center', gap: 12,
    }}>
      <Switch checked={checked} onChange={onChange} />
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 500, fontSize: 13 }}>{label}</div>
        {desc && <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{desc}</div>}
      </div>
    </div>
  );
}

function Field({ label, children }: any) {
  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}

/* ════════════════════════════════════════════════════════
 * v1.2-rc3 Layer-1：主管分配策略 + 主管池
 * ════════════════════════════════════════════════════════ */
function ManagerAllocationSection({ local, setLocal }: { local: any; setLocal: any }) {
  const qc = useQueryClient();
  const { data: pool } = useQuery<any[]>({
    queryKey: ['lead-manager-pool'],
    queryFn: () => $.get('/lead-manager-pool'),
  });
  const { data: usersList } = useQuery<any>({
    queryKey: ['users-managers-for-pool'],
    queryFn: () => $.get('/users?role=manager&pageSize=100'),
  });
  const allManagers = (usersList?.items ?? usersList ?? []) as any[];
  const inPoolIds = new Set((pool ?? []).map((p: any) => Number(p.managerUserId)));
  const candidatesToAdd = allManagers.filter((u: any) => !inPoolIds.has(Number(u.id)));

  const [adding, setAdding] = useState(false);
  const [pickAdd, setPickAdd] = useState<number | undefined>(undefined);

  const addRow = useMutation({
    mutationFn: () => $.post('/lead-manager-pool', { managerUserId: pickAdd }),
    onSuccess: () => {
      message.success('已加入主管池');
      setAdding(false); setPickAdd(undefined);
      qc.invalidateQueries({ queryKey: ['lead-manager-pool'] });
    },
    onError: (e: any) => message.error(e?.message ?? '添加失败'),
  });
  const updateRow = useMutation({
    mutationFn: ({ id, patch }: any) => $.put(`/lead-manager-pool/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lead-manager-pool'] }),
  });
  const deleteRow = useMutation({
    mutationFn: (id: number) => $.delete(`/lead-manager-pool/${id}`),
    onSuccess: () => {
      message.success('已移出');
      qc.invalidateQueries({ queryKey: ['lead-manager-pool'] });
    },
    // ux1：onOk 改 mutateAsync 后失败会 reject，补 onError toast 避免静默
    onError: (e: any) => message.error(e?.message ?? '移出失败'),
  });

  const recomputeTeam = useMutation({
    mutationFn: () => $.post('/team-performance-metrics/recompute', {}),
    onSuccess: (r: any) => message.success(`已重算 ${r.updated}/${r.scanned} 个团队`),
    onError: (e: any) => message.error(e?.message ?? '重算失败'),
  });

  const [teamPerfOpen, setTeamPerfOpen] = useState(false);

  const strategy = local.managerAllocationStrategy ?? 'disabled';
  const teamSum =
    Number(local.teamScoreWeightFollowup ?? 0) +
    Number(local.teamScoreWeightResponse ?? 0) +
    Number(local.teamScoreWeightRevenue ?? 0);

  // 策略 B 比例之和实时校验
  const enabledPool = (pool ?? []).filter((p: any) => p.enabled);
  const ratioSum = enabledPool.reduce((s: number, p: any) => s + Number(p.ratioPercent || 0), 0);

  return (
    <HomeCard title={<>第 1 段 · 主管分配策略 <Pill tone="primary">Layer-1</Pill></>}>
      <div style={{ padding: 16 }}>
        {/* 策略选择 */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8, fontSize: 13, color: 'var(--text-2)' }}>
            分配策略（决定线索给哪个主管）
          </div>
          <Radio.Group
            value={strategy}
            onChange={(e) => setLocal({ ...local, managerAllocationStrategy: e.target.value })}
          >
            <Radio.Button value="disabled">关闭（用账号 owner）</Radio.Button>
            <Radio.Button value="round_robin">平均轮流</Radio.Button>
            <Radio.Button value="ratio">按比例</Radio.Button>
            <Radio.Button value="team_score">按团队表现</Radio.Button>
          </Radio.Group>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 16, fontSize: 12 }}>
            统计窗口
            <Select
              value={local.managerAllocationWindow ?? 'month'}
              onChange={(v) => setLocal({ ...local, managerAllocationWindow: v })}
              style={{ width: 90, marginLeft: 6 }}
              options={[{ value: 'day', label: '日' }, { value: 'week', label: '周' }, { value: 'month', label: '月' }]}
              disabled={strategy === 'disabled'}
            />
          </span>
        </div>

        {/* 主管池 */}
        {strategy !== 'disabled' && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 13, color: 'var(--text-2)' }}>主管池（启用项参与分配）</span>
              {strategy === 'ratio' && (
                <span style={{ marginLeft: 12, fontSize: 12, color: ratioSum === 100 ? 'var(--success)' : 'var(--danger)' }}>
                  比例之和 {ratioSum} / 100{ratioSum !== 100 && '（必须等于 100）'}
                </span>
              )}
              <span style={{ flex: 1 }} />
              <Btn kind="default" onClick={() => setAdding(true)} >+ 添加主管</Btn>
            </div>
            <TableWrap>
              <table className="ck-table rows-static" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ width: 80 }}>启用</th>
                    <th>主管</th>
                    {strategy === 'ratio' && <th style={{ width: 200 }}>比例 %</th>}
                    <th style={{ width: 100 }}>本月已分到</th>
                    <th style={{ width: 90 }}>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {(pool ?? []).length === 0 && (
                    <tr><td colSpan={strategy === 'ratio' ? 5 : 4} style={{ padding: 20, textAlign: 'center', color: 'var(--text-3)' }}>
                      主管池为空 — 点上方"+ 添加主管"加入第一个主管
                    </td></tr>
                  )}
                  {(pool ?? []).map((p: any) => (
                    <tr key={p.id}>
                      <td><Switch checked={!!p.enabled} onChange={(v) =>
                        updateRow.mutate({ id: p.id, patch: { enabled: v ? 1 : 0 } })} /></td>
                      <td>
                        {p.managerName ?? `#${p.managerUserId}`}
                        {p.managerActive === false && <Pill tone="warning">已停用</Pill>}
                      </td>
                      {strategy === 'ratio' && (
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Slider min={0} max={100} value={p.ratioPercent}
                              onChange={(v) => updateRow.mutate({ id: p.id, patch: { ratioPercent: v } })}
                              tooltip={{ open: false }} style={{ flex: 1 }} />
                            <span style={{ minWidth: 36, textAlign: 'right', fontSize: 12 }}>{p.ratioPercent}%</span>
                          </div>
                        </td>
                      )}
                      <td><Pill tone="default">{p.leadsThisMonth}</Pill></td>
                      <td>
                        <Btn kind="default" onClick={() => {
                          Modal.confirm({
                            title: '从主管池移出？',
                            content: `主管「${p.managerName}」将不再参与分配（历史 lead 不受影响）`,
                            onOk: () => deleteRow.mutateAsync(p.id),
                          });
                        }}>移出</Btn>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWrap>
          </div>
        )}

        {/* 策略 C 专属：3 维子权重 + 保底 */}
        {strategy === 'team_score' && (
          <div style={{ padding: 12, background: 'var(--bg-2)', borderRadius: 6, marginBottom: 12 }}>
            <div style={{ marginBottom: 8, fontSize: 13 }}>
              <strong>团队打分维度权重</strong>{' '}
              <span style={{ fontSize: 12, color: teamSum === 100 ? 'var(--success)' : 'var(--danger)' }}>
                ─ 总和 {teamSum} / 100
              </span>
            </div>
            <SubWeight label="跟进完整率" hint="本月有跟进客户数 / 持客数"
              value={local.teamScoreWeightFollowup}
              onChange={(v: number) => setLocal({ ...local, teamScoreWeightFollowup: v })} />
            <SubWeight label="响应程度" hint="接单响应秒数 + 24h 内添加客户比例"
              value={local.teamScoreWeightResponse}
              onChange={(v: number) => setLocal({ ...local, teamScoreWeightResponse: v })} />
            <SubWeight label="出单情况" hint="本月营收 / 团队目标（无目标按 50000 基准）"
              value={local.teamScoreWeightRevenue}
              onChange={(v: number) => setLocal({ ...local, teamScoreWeightRevenue: v })} />
            <div style={{ marginTop: 10, borderTop: '1px dashed var(--border)', paddingTop: 10 }}>
              <SubWeight label="保底配额 %"
                hint="每个启用主管至少拿到的比例（差团队不至于断粮，0-50）"
                value={local.teamScoreFloorPercent} onChange={(v: number) => setLocal({ ...local, teamScoreFloorPercent: v })}
                max={50} />
            </div>
            <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
              <Btn kind="default" onClick={() => setTeamPerfOpen(true)}>📊 查看当月各团队分</Btn>
              <Btn kind="default" onClick={() => recomputeTeam.mutate()} disabled={recomputeTeam.isPending}>
                {recomputeTeam.isPending ? '重算中…' : '🔄 立即重算'}
              </Btn>
            </div>
          </div>
        )}

        {/* 兜底说明 */}
        {strategy !== 'disabled' && (
          <div style={{
            padding: 10, background: 'rgba(124,77,255,0.06)',
            borderRadius: 4, fontSize: 12, color: 'var(--text-2)',
          }}>
            💡 主管池为空或全部停用时，自动回退到 <code>marketing_accounts.owner_user_id</code> 旧逻辑（不会断线索）。
          </div>
        )}
      </div>

      {/* 添加主管 Modal */}
      <Modal title="添加主管到分配池" open={adding}
        onCancel={() => { setAdding(false); setPickAdd(undefined); }}
        footer={
          <>
            <Btn kind="default" onClick={() => { setAdding(false); setPickAdd(undefined); }}>取消</Btn>
            <Btn kind="primary" disabled={!pickAdd} onClick={() => addRow.mutate()}>加入</Btn>
          </>
        }>
        <Select value={pickAdd} onChange={(v) => setPickAdd(Number(v))} style={{ width: '100%' }}
          placeholder="选 manager 角色用户" showSearch
          filterOption={(input, opt) => String(opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
          options={candidatesToAdd.map((u: any) => ({
            value: Number(u.id), label: `${u.realName}（#${u.id}）`,
          }))} />
        {candidatesToAdd.length === 0 && (
          <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-3)' }}>所有 manager 已在池中</div>
        )}
      </Modal>

      {/* 团队分预览 Modal */}
      <TeamPerfModal open={teamPerfOpen} onClose={() => setTeamPerfOpen(false)} />
    </HomeCard>
  );
}

function SubWeight({ label, hint, value, onChange, max = 100 }: any) {
  return (
    <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
      <span style={{ minWidth: 100, fontSize: 13 }}>{label}</span>
      <span style={{ fontSize: 11, color: 'var(--text-3)', flex: 1 }}>{hint}</span>
      <Slider min={0} max={max} value={value}
        onChange={(v: number) => onChange(v)}
        style={{ width: 160 }} tooltip={{ open: false }} />
      <span style={{ minWidth: 36, textAlign: 'right' }}>{value}%</span>
    </div>
  );
}

function TeamPerfModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data } = useQuery<any[]>({
    queryKey: ['team-perf-current'],
    queryFn: () => $.get('/team-performance-metrics'),
    enabled: open,
  });
  return (
    <Modal title="本月各团队表现" open={open} onCancel={onClose} footer={null} width={720}>
      <TableWrap>
        <table className="ck-table rows-static" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>主管</th><th>团队人数</th><th>跟进完整</th><th>响应程度</th><th>出单</th><th>综合分</th>
            </tr>
          </thead>
          <tbody>
            {(data ?? []).length === 0 && (
              <tr><td colSpan={6} style={{ padding: 20, textAlign: 'center', color: 'var(--text-3)' }}>
                暂无数据，点"立即重算"或等明早 07:00 cron
              </td></tr>
            )}
            {(data ?? []).map((m: any) => (
              <tr key={m.id}>
                <td>{m.managerName ?? `#${m.managerUserId}`}</td>
                <td>{m.salesCount}</td>
                <td>{(Number(m.followupCompletion) * 100).toFixed(1)}%</td>
                <td>{(Number(m.responseScore) * 100).toFixed(1)}%</td>
                <td>¥{Number(m.totalRevenue).toLocaleString()}</td>
                <td><Pill tone="primary">{Number(m.compositeScore).toFixed(1)}</Pill></td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableWrap>
    </Modal>
  );
}

function PreviewPanel() {
  // 简化：列出当前 sales_performance_metrics top 10
  // 暂用 /users 列表作为占位（避免引入新 endpoint）
  const { data: users } = useQuery<any>({
    queryKey: ['users-list-perf'],
    queryFn: () => $.get('/users?role=sales&pageSize=50'),
  });
  const items = (users?.items ?? users ?? []).slice(0, 10);
  return (
    <div style={{ padding: 12 }}>
      {items.length === 0
        ? <div style={{ color: 'var(--text-3)', textAlign: 'center', padding: 20 }}>暂无销售员数据</div>
        : items.map((u: any, i: number) => (
          <div key={u.id} style={{
            padding: '8px 12px', borderBottom: '1px solid var(--border)',
            display: 'flex', gap: 10, alignItems: 'center',
          }}>
            <span style={{
              width: 22, height: 22, lineHeight: '22px', textAlign: 'center', borderRadius: 11,
              background: i < 3 ? 'var(--warning)' : 'var(--bg-3)',
              color: i < 3 ? '#fff' : 'var(--text-2)', fontSize: 11, fontWeight: 600,
            }}>{i + 1}</span>
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.realName}</span>
            <Pill tone="default">持客 {Math.max(0, Number(u.currentCustomerCount ?? 0))}</Pill>
          </div>
        ))}
      <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-3)' }}>
        提示：实际分配按 sales_performance_metrics.composite_score 排（cron 每天 06:00 重算）
      </div>
    </div>
  );
}
